import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUserProfile } from '@/lib/data-access/user-profile';
import { getUserTrackSelection } from '@/lib/data-access/user-track';

interface OnboardingStatus {
  isLoading: boolean;
  needsProfile: boolean;
  needsTrack: boolean;
  isComplete: boolean;
}

/**
 * Hook to check if user has completed onboarding and redirect if needed.
 * Should be used in protected routes that require a complete profile.
 */
export function useOnboardingGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<OnboardingStatus>({
    isLoading: true,
    needsProfile: false,
    needsTrack: false,
    isComplete: false,
  });

  useEffect(() => {
    // Don't check if we're already on an onboarding page
    if (location.pathname.startsWith('/onboarding')) {
      setStatus({ isLoading: false, needsProfile: false, needsTrack: false, isComplete: true });
      return;
    }

    async function checkOnboardingStatus() {
      try {
        // Check if user has a profile
        const profile = await getCurrentUserProfile();
        
        if (!profile) {
          setStatus({ isLoading: false, needsProfile: true, needsTrack: true, isComplete: false });
          navigate('/onboarding', { replace: true });
          return;
        }

        // Check if user has selected a track
        const trackSelection = await getUserTrackSelection();
        
        if (!trackSelection) {
          setStatus({ isLoading: false, needsProfile: false, needsTrack: true, isComplete: false });
          navigate('/onboarding/track', { replace: true });
          return;
        }

        // Onboarding is complete
        setStatus({ isLoading: false, needsProfile: false, needsTrack: false, isComplete: true });
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setStatus({ isLoading: false, needsProfile: false, needsTrack: false, isComplete: true });
      }
    }

    checkOnboardingStatus();
  }, [navigate, location.pathname]);

  return status;
}
